import { apiRequest } from '../../../shared/services/apiClient';
import type {
  AiToolOrchestrator,
  AiToolOrchestratorCatalogItem,
  CreateAiToolOrchestratorRequest,
  PreviewAiToolOrchestratorRequest,
  UpdateAiToolOrchestratorRequest,
} from '../types/Orchestrator';

const normalizeParameter = (value: any) => ({
  name: String(value?.name ?? ''),
  description: String(value?.description ?? ''),
  isRequired: Boolean(value?.isRequired ?? false),
  defaultValue: String(value?.defaultValue ?? ''),
  example: String(value?.example ?? ''),
});

const normalizeOrchestrator = (value: any): AiToolOrchestrator => ({
  orchestratorId: String(value?.orchestratorId ?? ''),
  kernelFunctionName: String(value?.kernelFunctionName ?? ''),
  displayName: String(value?.displayName ?? ''),
  description: String(value?.description ?? ''),
  isPublished: Boolean(value?.isPublished ?? false),
  childFunctionIds: Array.isArray(value?.childFunctionIds) ? value.childFunctionIds.map(String) : [],
  systemPrompt: String(value?.systemPrompt ?? ''),
  argumentMode: String(value?.argumentMode ?? 'forward'),
  parameters: Array.isArray(value?.parameters) ? value.parameters.map(normalizeParameter) : [],
  subagentChatProvider: String(value?.subagentChatProvider ?? 'inherit'),
  subagentModelId: value?.subagentModelId ? String(value.subagentModelId) : null,
  maxChildToolCalls: Number(value?.maxChildToolCalls ?? 5),
  maxOutputTokens: value?.maxOutputTokens == null ? null : Number(value.maxOutputTokens),
  timeoutSeconds: value?.timeoutSeconds == null ? null : Number(value.timeoutSeconds),
  forwardedParameterName: String(value?.forwardedParameterName ?? 'query'),
  functionId: String(value?.functionId ?? (value?.orchestratorId ? `orchestrator.${value.orchestratorId}` : '')),
});

const normalizeCatalogItem = (value: any): AiToolOrchestratorCatalogItem => ({
  functionId: String(value?.functionId ?? ''),
  displayName: String(value?.displayName ?? ''),
  source: String(value?.source ?? ''),
  accessMode: String(value?.accessMode ?? 'read'),
  isAssignableAsChild: Boolean(value?.isAssignableAsChild ?? true),
  assignedToOrchestratorId: value?.assignedToOrchestratorId ? String(value.assignedToOrchestratorId) : null,
  assignedToOrchestratorName: value?.assignedToOrchestratorName ? String(value.assignedToOrchestratorName) : null,
});

export const listAiToolOrchestrators = async (): Promise<AiToolOrchestrator[]> => {
  const result = await apiRequest<unknown[]>('/api/v1/ai/orchestrators', { method: 'GET' });
  return Array.isArray(result) ? result.map(normalizeOrchestrator) : [];
};

export const getAiToolOrchestratorCatalog = async (): Promise<AiToolOrchestratorCatalogItem[]> => {
  const result = await apiRequest<unknown[]>('/api/v1/ai/orchestrators/tool-catalog', { method: 'GET' });
  return Array.isArray(result) ? result.map(normalizeCatalogItem) : [];
};

export const createAiToolOrchestrator = async (
  request: CreateAiToolOrchestratorRequest,
): Promise<AiToolOrchestrator> => {
  const result = await apiRequest<unknown>('/api/v1/ai/orchestrators', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  return normalizeOrchestrator(result);
};

export const updateAiToolOrchestrator = async (
  orchestratorId: string,
  request: UpdateAiToolOrchestratorRequest,
): Promise<AiToolOrchestrator> => {
  const result = await apiRequest<unknown>(
    `/api/v1/ai/orchestrators/${encodeURIComponent(orchestratorId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
  );
  return normalizeOrchestrator(result);
};

export const deleteAiToolOrchestrator = async (orchestratorId: string): Promise<void> => {
  await apiRequest<void>(
    `/api/v1/ai/orchestrators/${encodeURIComponent(orchestratorId)}`,
    { method: 'DELETE' },
  );
};

export const previewAiToolOrchestrator = async (
  orchestratorId: string,
  request: PreviewAiToolOrchestratorRequest,
): Promise<string> => {
  return apiRequest<string>(
    `/api/v1/ai/orchestrators/${encodeURIComponent(orchestratorId)}/preview`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
  );
};
